import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import LogoMark from "@/components/LogoMark";
import { waLink } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const ornamentY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-28 md:pt-36">
      <div
        className="pointer-events-none absolute -top-40 right-[-10%] h-[42rem] w-[42rem] rounded-full opacity-20 blur-[120px]"
        style={{ background: "radial-gradient(circle, #D4AF37 0%, transparent 65%)" }}
      />
      <motion.div
        style={{ y: ornamentY }}
        className="pointer-events-none absolute -left-24 top-1/3 opacity-[0.07]"
        aria-hidden="true"
      >
        <LogoMark className="h-96 w-96 animate-spin-slow" />
      </motion.div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-20 md:px-10 lg:grid-cols-12 lg:gap-8 lg:pb-28">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="mb-7 flex flex-wrap items-center gap-3 text-xs font-light uppercase tracking-[0.35em] text-gold"
            data-testid="hero-eyebrow"
          >
            <span className="font-gurmukhi text-base normal-case tracking-normal">ਸਤ ਸ੍ਰੀ ਅਕਾਲ</span>
            <span className="h-px w-10 bg-gold/50" />
            Willkommen bei APSARA Kollektion
          </motion.p>

          <h1
            data-testid="hero-title"
            className="font-serif text-[15vw] font-light leading-[0.95] tracking-[-0.02em] text-cream sm:text-[11vw] lg:text-[6.4vw]"
          >
            <MaskedLine delay={0.25}>Königliche</MaskedLine>
            <MaskedLine delay={0.38}>Eleganz.</MaskedLine>
            <MaskedLine delay={0.51}>
              <span className="font-normal">
                Für die moderne <em className="text-goldgrad not-italic md:italic">Maharani</em>.
              </span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
            className="mt-8 max-w-md text-base font-light leading-relaxed text-rosy md:text-lg"
            data-testid="hero-subline"
          >
            Handgefertigter Schmuck &amp; feinste Phulkari-Suits. Eine Ode an die
            punjabische Tradition — mit Liebe kuratiert, von Mutter zu Tochter.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#schmuck"
              data-testid="hero-cta-kollektion"
              className="group flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-medium uppercase tracking-widest text-wine transition-colors duration-300 hover:bg-goldlt"
            >
              Kollektion entdecken
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="flex items-center gap-2 rounded-full border border-gold/60 px-8 py-4 text-sm font-medium uppercase tracking-widest text-gold transition-colors duration-300 hover:bg-gold hover:text-wine"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp schreiben
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-8 text-xs font-light uppercase tracking-[0.25em] text-cream/50"
            data-testid="hero-trustline"
          >
            Persönliche Beratung · Individuelle Anfertigung
          </motion.p>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.3, delay: 0.55, ease: EASE }}
            className="relative mx-auto max-w-sm lg:max-w-none"
            data-testid="hero-image-frame"
          >
            <div className="absolute -inset-3 rounded-t-[12rem] rounded-b-lg border border-gold/25" aria-hidden="true" />
            <motion.div style={{ y: imgY }} className="overflow-hidden rounded-t-[12rem] rounded-b-lg border border-white/10">
              <img
                src="https://images.unsplash.com/photo-1600685890506-593fdf55949b?q=80&w=1200&auto=format&fit=crop"
                alt="Braut mit goldenem punjabischem Schmuck"
                className="h-[60vh] w-full max-h-[80vh] object-cover md:h-[72vh]"
                data-testid="hero-image"
              />
            </motion.div>
            <div className="absolute -bottom-6 -left-6 rounded-lg border border-gold/25 bg-wine/80 px-6 py-4 backdrop-blur-md">
              <p className="font-gurmukhi text-lg text-gold">ਪਿਆਰ</p>
              <p className="text-xs font-light uppercase tracking-[0.25em] text-cream/70">
                Mit Pyaar — mit Liebe
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
